/**
 * TrustGrid Design Tokens
 * Raw design values for colors, spacing, typography, etc.
 * These tokens feed into antdTheme.js and globalStyles.css
 */

// ==================== COLORS ====================

// Brand Colors
export const colors = {
  // Primary brand (Truvex dark navy)
  primary: {
    50: '#e8eaec',
    100: '#c6c9ce',
    200: '#a0a5ad',
    300: '#79818c',
    400: '#5d6673',
    500: '#17052B', // Truvex brand color
    600: '#140426',
    700: '#110420',
    800: '#0d031a',
    900: '#07010f',
  },

  // Status Colors (Refined - calmer, less aggressive)
  status: {
    pass: '#008A2A',      // Icon/text - strong success tone
    passLight: '#72D69A', // Border - more defined edge
    passBg: '#C4F7D5',    // Background - light mint base (modern)

    fail: '#CE3E57',      // Icon color - less harsh red
    failLight: '#CE3E57', // Text/border color
    failBg: '#F9E7E9',    // Background - less "error alarm"

    warning: '#C89122',     // Icon color - darker amber
    warningLight: '#C89122', // Text/border color
    warningBg: '#FBEFCE',    // Background - softer yellow

    info: '#0288d1',      // Blue
    infoBg: '#E4F2FF',    // Background - less attention-grabbing

    running: '#0288d1',   // Running state (same as info)
    runningBg: '#E4F2FF', // Background - less attention-grabbing

    unknown: '#757575',   // Gray
    unknownBg: '#f5f5f5', // Gray background
  },

  // Severity Colors (Critical/High/Medium/Low) - Refined
  severity: {
    critical: {
      main: '#CE3E57',    // Less harsh red
      bg: '#F8D8DD',      // Softer background
      border: '#F8D8DD',
    },
    high: {
      main: '#f57c00',
      bg: '#fff3e0',
      border: '#ffcc80',
    },
    medium: {
      main: '#C89122',    // Amber
      bg: '#FBEFCE',      // Softer yellow
      border: '#FBEFCE',
    },
    low: {
      main: '#008A2A',    // Strong success tone
      bg: '#C4F7D5',      // Light mint base (modern)
      border: '#72D69A',  // More defined edge
    },
  },

  // Trust Score Gradient (tier-based color mapping)
  trustScore: {
    excellent: '#008A2A', // 80-100: Healthy/Strong (affirmative success)
    good: '#C89122',      // 60-79: Needs attention
    poor: '#CE3E57',      // < 60: High Risk
  },

  // Neutrals (Grays)
  neutral: {
    0: '#ffffff',
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
    1000: '#000000',
  },

  // Text Colors
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
    disabled: 'rgba(0, 0, 0, 0.38)',
    hint: '#888888',
    inverse: '#ffffff',
  },

  // Backgrounds
  background: {
    default: '#ffffff', // Pure white content area
    paper: '#ffffff',
    elevated: '#ffffff',
    subtle: '#fafafa', // Very light gray for alternate sections
  },

  // Borders (warmer neutral for premium feel)
  border: {
    light: '#EBE8F0',    // Warmer, calmer border for cards
    main: '#EBEBEB',     // Cleaner, more neutral divider (less gray/purple)
    dark: '#bdbdbd',
  },

  // Sidebar Colors (Two-Panel Navigation) - Dark theme with Truvex brand
  sidebar: {
    primary: '#17052B',          // Primary rail - Truvex brand color
    primaryHover: '#2A1046',     // Primary hover state
    primaryActive: '#3D1A5C',    // Primary active state
    secondary: '#221338',        // Secondary panel - slightly lighter for depth
    secondaryHover: '#2D1B47',   // Secondary hover state
    edge: '#3D1A5C',             // Border between surfaces
    divider: 'rgba(255, 250, 234, 0.08)', // Subtle divider lines

    // Widths
    railCollapsed: 56,           // Icon-only width
    railExpanded: 200,           // Icon + text width
    panelWidth: 240,             // Secondary panel width
  },

  // Brand Colors (Logo & Identity)
  brand: {
    ivory: '#FFFAEA',           // Brand color for logos on dark backgrounds
    ink: '#17052B',             // Primary brand color
    inkSoft: 'rgba(23, 5, 43, 0.75)',  // Softer icon stroke for premium feel
    inkMedium: 'rgba(23, 5, 43, 0.85)', // Header text
    accent: '#8B5CF6',          // Purple accent for interactive elements
  },

  // Interactive States
  interactive: {
    hover: 'rgba(255, 250, 234, 0.06)',      // Hover overlay on dark
    active: '#2A1946',                        // Active background (legacy)
    hoverDark: 'rgba(255, 250, 234, 0.06)',  // Dark sidebar hover
    activeDark: '#3D1A5C',                    // Dark sidebar active
    textPrimary: '#FFFAEA',                   // Primary text on dark
    textSecondary: 'rgba(255, 250, 234, 0.7)', // Secondary text on dark
    textMuted: 'rgba(255, 250, 234, 0.5)',    // Muted text on dark
  },

  // Text on Dark Surfaces (opacity levels)
  onSurface: {
    primary: 'rgba(255, 250, 234, 0.84)',   // 84% - primary text
    secondary: 'rgba(255, 250, 234, 0.72)', // 72% - secondary text
    disabled: 'rgba(255, 250, 234, 0.56)',  // 56% - disabled text
  },

  // Form-specific colors (Premium design system)
  form: {
    inputBorder: '#E4E1E8',          // Default border - light and subtle
    inputBorderFocus: '#4B3781',     // Focus border - brand purple
    inputBackground: '#FFFFFF',       // Input background - pure white
    inputPlaceholder: '#A29DB5',      // Placeholder text - light but readable
    hoverBackground: '#F4F1FA',       // Hover background - soft lavender
    hoverBackgroundLight: '#F7F5FC',  // Even lighter hover
    selectedBackground: '#F4F1FA',    // Selected row background
    selectedText: '#17052B',          // Selected row text - dark
    badgeBackground: '#F4F1FA',       // Badge/tag background
    badgeBorder: '#E4E1E8',          // Badge border
    iconMuted: '#6A608A',            // Muted icon color
  },
};

// ==================== SPACING ====================

// Spacing scale (based on 4px/8px grid)
export const spacing = {
  none: 0,
  xxs: 4,    // 0.25rem
  xs: 8,     // 0.5rem
  sm: 12,    // 0.75rem
  md: 16,    // 1rem
  lg: 24,    // 1.5rem
  xl: 32,    // 2rem
  xxl: 48,   // 3rem
  xxxl: 64,  // 4rem
};

// Common spacing values (for quick access)
export const space = {
  0: spacing.none,
  1: spacing.xxs,
  2: spacing.xs,
  3: spacing.sm,
  4: spacing.md,
  5: spacing.lg,
  6: spacing.xl,
  7: spacing.xxl,
  8: spacing.xxxl,
};

// ==================== TYPOGRAPHY ====================

export const typography = {
  // Font families
  fontFamily: {
    base: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    heading: 'Bellefair, Georgia, serif', // For H1/Hero titles only
    mono: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, Courier, monospace',
  },

  // Font sizes
  fontSize: {
    xs: 12,    // Small text, captions
    sm: 13,    // Secondary text
    base: 13,  // Body text
    md: 15,    // Emphasized body
    lg: 16,    // Large text
    xl: 18,    // H3
    xxl: 20,   // H2
    xxxl: 24,  // H1
  },

  // Font weights
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Line heights
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
    loose: 2,
  },

  // Headings
  headings: {
    h1: {
      fontSize: 24,
      fontWeight: 600,
      lineHeight: 1.2,
      fontFamily: 'Inter',
    },
    h2: {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: 1.2,
      fontFamily: 'Inter',
    },
    h3: {
      fontSize: 18,
      fontWeight: 600,
      lineHeight: 1.3,
      fontFamily: 'Inter',
    },
    h4: {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: 1.4,
      fontFamily: 'Inter',
    },
    h5: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 1.5,
      fontFamily: 'Inter',
    },
  },
};

// ==================== SHADOWS ====================

export const shadows = {
  none: 'none',
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.03)', // Reduced from 0.05
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.04)', // Reduced, subtle
  md: '0 2px 4px 0 rgba(0, 0, 0, 0.05)', // Reduced
  lg: '0 4px 8px 0 rgba(0, 0, 0, 0.06)', // Reduced
  xl: '0 8px 16px 0 rgba(0, 0, 0, 0.07)', // Reduced
  xxl: '0 12px 24px 0 rgba(0, 0, 0, 0.08)', // Reduced
  // Form-specific shadows
  popover: '0 2px 12px rgba(0, 0, 0, 0.06)', // Subtle shadow for popovers
  dropdown: '0 2px 8px rgba(0, 0, 0, 0.06)',  // Subtle shadow for dropdowns
};

// ==================== BORDER RADIUS ====================

export const borderRadius = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  xxl: 24,
  full: 9999,
  // Form-specific
  input: 8,      // Input fields
  popover: 12,   // Dropdown popovers
  dropdown: 10,  // Dropdown containers
  badge: 6,      // Badges/tags
};

// ==================== Z-INDEX ====================

export const zIndex = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

// ==================== TRANSITIONS ====================

export const transitions = {
  duration: {
    fast: '150ms',
    base: '200ms',
    slow: '300ms',
    slower: '500ms',
  },
  easing: {
    linear: 'linear',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
};

// ==================== BREAKPOINTS ====================

export const breakpoints = {
  xs: 480,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
};

// ==================== COMPONENT SIZES ====================

export const sizes = {
  sidebar: {
    collapsed: 80,
    expanded: 250,
  },
  header: {
    height: 64,
  },
  button: {
    sm: 24,
    md: 32,
    lg: 40,
  },
  input: {
    sm: 24,
    md: 32,
    lg: 40,
  },
};

// ==================== EXPORTS ====================

export default {
  colors,
  spacing,
  space,
  typography,
  shadows,
  borderRadius,
  zIndex,
  transitions,
  breakpoints,
  sizes,
};
